import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import utils from 'web3-utils';
import modalify from '@blockcatio/tabby-library/lib/utilities/modalify';
import Spinner from '@blockcatio/tabby-library/lib/components/Spinner';
import safeToBn from '@blockcatio/tabby-library/lib/utilities/safeToBn';
import EthereumAddress from '@blockcatio/tabby-library/lib/components/EthereumAddress';
import { noop } from 'lodash';

import PaymentAddress from './PaymentAddress';
import TimeDisplay from './TimeDisplay';
import eventTracker from '../services/EventTrackerInstance';
import RemovePaymentModal from '../components/RemovePaymentModal';
import {
  TRACKER_TYPES,
  EVENT_TRACKER_ACTIONS,
  BACKUP_GAS_PRICE,
} from '../constants';

const { event } = TRACKER_TYPES;
const {
  acceptButtonClicked,
  acceptPaymentFail,
  handleAccept,
  acceptPaymentSuccess,
  cancelButtonClicked,
  cancelPaymentFail,
  handleCancel,
  cancelPaymentSuccess,
} = EVENT_TRACKER_ACTIONS;

class Payment extends React.Component {
  static defaultProps = {
    cancelPayment: () => Promise.resolve(''),
    acceptPayment: () => Promise.resolve(''),
    onPaymentRemove: noop,
    recommendedGasPrice: 0,
    notify: noop,
    formatPaymentAmount: noop,
  };

  constructor(props) {
    super(props);

    this.state = {
      // Allow for tracking the current action, and the status code for it,
      // we will know an action is happening if actionStatus isn't the current status.
      actionStatus: props.payment.status,
      currentStatus: props.payment.status,
    };

    this.handleAccept = this.handleAccept.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.processPayment = this.processPayment.bind(this);
    this.generateRenderData = this.generateRenderData.bind(this);
    this.handlePaymentRemove = this.handlePaymentRemove.bind(this);
    this.copyNotification = () => this.props.notify('Copied to clipboard');
  }

  componentWillReceiveProps(nextProps) {
    // Reset actionStatus when the payment status changes.
    if (nextProps.payment.status !== this.state.currentStatus) {
      this.setState({
        actionStatus: nextProps.payment.status,
        currentStatus: nextProps.payment.status,
      });
    }
  }

  async handleAccept() {
    const {
      payment: { sender, amount, tokenAddress },
    } = this.props;
    eventTracker.track(event, handleAccept);
    const viewOptions = {
      title: 'Accept Payment',
      message: (
        <React.Fragment>
          Are you sure you&apos;d like to accept this payment?
          <br />
          This action cannot be undone.
        </React.Fragment>
      ),
      extraFields: {
        Sender: {
          rowClass: 'address-row',
          component: (
            <div>
              <EthereumAddress address={sender} />
            </div>
          ),
        },
        Accepting: {
          component: (
            <span className="amount">
              {this.props.formatPaymentAmount(safeToBn(amount), tokenAddress)}
            </span>
          ),
        },
      },
      disclaimers: ['Note: you will pay a small gas fee for the transaction.'],
    };
    const transactionHash = await this.processPayment(
      this.props.acceptPayment,
      BACKUP_GAS_PRICE,
      viewOptions,
    );
    if (transactionHash) {
      eventTracker.track(event, acceptPaymentSuccess);
    } else {
      eventTracker.track(event, acceptPaymentFail);
    }
  }

  async handleCancel() {
    const {
      payment: { sender, amount, tokenAddress },
    } = this.props;
    const viewOptions = {
      title: 'Cancel Payment',
      message: (
        <React.Fragment>
          Are you sure you&apos;d like to cancel this payment?
          <br />
          This action cannot be undone.
        </React.Fragment>
      ),
      extraFields: {
        Sender: {
          rowClass: 'address-row',
          component: (
            <div>
              <EthereumAddress address={sender} />
            </div>
          ),
        },
        Reversing: {
          component: (
            <span className="amount">
              {this.props.formatPaymentAmount(safeToBn(amount), tokenAddress)}
            </span>
          ),
        },
      },
      disclaimers: ['Note: you will pay a small gas fee for the transaction.'],
    };
    eventTracker.track(event, handleCancel);
    const transactionHash = await this.processPayment(
      this.props.cancelPayment,
      BACKUP_GAS_PRICE,
      viewOptions,
    );
    if (transactionHash) {
      eventTracker.track(event, cancelPaymentSuccess);
    } else {
      eventTracker.track(event, cancelPaymentFail);
    }
  }

  async processPayment(func, defaultGasPrice, viewOptions) {
    const { payment, recommendedGasPrice } = this.props;
    const gasPrice = recommendedGasPrice || defaultGasPrice;
    const gasPriceInWei = gasPrice ? utils.toWei(gasPrice.toString(), 'gwei') : '';

    return func(payment, gasPriceInWei, viewOptions);
  }

  async handlePaymentRemove() {
    const {
      payment: { sender, destination },
    } = this.props;
    const confirmRemove = modalify(RemovePaymentModal);
    if (
      await confirmRemove({
        sender,
        destination,
      })
    ) {
      this.props.onPaymentRemove(this.props.payment);
    }
  }

  generateRenderData() {
    const {
      payment: {
        id,
        destination,
        sender,
        status,
        amount,
        tokenAddress,
        sentTimestamp,
        type,
        transactionHash,
      },
    } = this.props;

    const { actionStatus } = this.state;
    const transactionDate = moment.unix(sentTimestamp).toDate();

    let cancelButton = null;
    let acceptButton = null;
    let removeButton = null;

    if (type === 'incoming' && status.isAcceptable()) {
      const buttonId = `accept-button-${id}`;
      acceptButton = (
        <button
          name="accept"
          className="btn small"
          disabled={actionStatus !== status}
          title="Deposit payment into your wallet"
          onClick={() => {
            eventTracker.track(event, acceptButtonClicked);
            this.handleAccept();
          }}
          id={buttonId}
        >
          Accept
        </button>
      );
    }

    if (status.isCancellable()) {
      const buttonId = `cancel-button-${id}`;
      cancelButton = (
        <button
          name="cancel"
          className="btn small cancel"
          disabled={actionStatus !== status}
          title="Refund payment to sender"
          onClick={() => {
            eventTracker.track(event, cancelButtonClicked);
            this.handleCancel();
          }}
          id={buttonId}
        >
          Cancel
        </button>
      );
    }

    if (status.isRemovable()) {
      const buttonId = `remove-button-${id}`;
      removeButton = (
        <button
          name="remove"
          className="btn small remove"
          disabled={actionStatus !== status}
          title="Remove payment from list"
          onClick={this.handlePaymentRemove}
          id={buttonId}
        >
          Remove
        </button>
      );
    }

    const paymentStatus = status;
    const classes = [paymentStatus.toString(), type.toLowerCase()];

    const statusImage = <Spinner loading={status.isTransitional()} small message="Loading..." />;

    return {
      id,
      sender,
      destination,
      type,
      transactionDate,
      amount: safeToBn(amount),
      tokenAddress,
      paymentStatus,
      className: classes.join(' '),
      statusImage,
      acceptButton,
      cancelButton,
      removeButton,
      transactionHash,
    };
  }

  render() {
    const {
      id,
      sender,
      destination,
      type,
      transactionDate,
      amount,
      tokenAddress,
      paymentStatus,
      className,
      statusImage,
      acceptButton,
      cancelButton,
      removeButton,
      transactionHash,
    } = this.generateRenderData();

    const txId = `tx-${transactionHash}`;
    return (
      <tr className={className} id={txId}>
        <td className="id">{id}</td>
        <td className="time">
          <TimeDisplay date={transactionDate} />
        </td>
        <td className="to">
          <PaymentAddress address={destination} onCopy={this.copyNotification} />
        </td>
        <td className="from">
          <PaymentAddress address={sender} onCopy={this.copyNotification} />
        </td>
        <td className="type">{type}</td>
        <td className="amount">{this.props.formatPaymentAmount(amount, tokenAddress)}</td>
        <td className="status">
          {paymentStatus.toString()}
          {statusImage}
        </td>
        <td className="options">
          {acceptButton}
          {cancelButton}
          {removeButton}
        </td>
      </tr>
    );
  }
}

Payment.propTypes = {
  payment: PropTypes.shape({
    destination: PropTypes.string.isRequired,
    sender: PropTypes.string.isRequired,
    amount: PropTypes.string.isRequired,
    id: PropTypes.string,
    type: PropTypes.string.isRequired,
    status: PropTypes.shape({}),
    transactionHash: PropTypes.string.isRequired,
    sentTimestamp: PropTypes.number,
  }).isRequired,
  acceptPayment: PropTypes.func,
  cancelPayment: PropTypes.func,
  onPaymentRemove: PropTypes.func,
  recommendedGasPrice: PropTypes.number,
  notify: PropTypes.func,
  formatPaymentAmount: PropTypes.func,
};

export default Payment;



// WEBPACK FOOTER //
// ./src/components/Payment.js